import groupBy from 'lodash/groupBy';
import remove from 'lodash/remove';
import { ChangeEvent } from 'react';
import { action, computed, IObservableArray, makeObservable, observable } from 'mobx';
import { documentDragErrorMessage, isDocumentFile, Nullable, pluralize, SUPPORTED_DOCUMENT_TYPES_LABEL } from '../../core';
import { UploadTask } from '../../services/upload';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { input, inputGroup, InputGroupState, InputState } from '../../components/input';
import { createArray } from '../../core/array';
import { DocumentUploadFileItemState } from './documentUploadFileItemState';
import { GroupCriteria, UploadTaskGroup } from '../../services/upload/uploadTaskGroup';

type Props = BindingProps<{
  teamId?: string;
}>;

export class DocumentUploadFileState
  extends StoreNode {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      stagedItems: computed,
      activeItems: computed,
      completedItems: computed,
      teamId: computed,
      teamName: computed,
      isHovered: observable,
      isDragHovered: observable,
      expandedBoxes: observable,
      expandProgressBox: action,
      collapseProgressBox: action,
      toggleProgressBox: action,
      dragError: computed,
      dragErrorMessage: computed,
      dragDropMessage: computed,
      teamGroupedTasks: computed,
      tasksByTeam: computed,
      taskTeamIds: computed,
      showStagingForm: computed,
      showProgressBox: computed,
      stagingBoxesCount: computed,
      handlePointerEnter: action,
      handlePointerLeave: action,
      handleFileChange: action,
      handleFileClick: action,
      handleDragEnter: action,
      handleDragLeave: action,
      handleDrop: action,
      setUploadInput: action,
      addFileItem: action,
      removeFileItem: action,
      submit: action,
      init: action,
      reset: action,
    });

    this.form = inputGroup(this, {
      name: 'form',
      inputs: () =>
        this.stagedItems.map((el: DocumentUploadFileItemState) => el.form),
      isSubmitDisabled: () => {
        return (
          this.form.inputs.some(inp =>
            // @ts-expect-error -- InputState.inputs exists on InputGroupState but not on the union type
            inp.inputs.some(inp => inp.status === 'error' && inp.isTouched && !inp.isFocused)
          ) ||
          this.form.isSubmitting ||
          this.form.isEmpty
        );
      }
    });
  }

  readonly form: InputGroupState;

  get stagedItems(): DocumentUploadFileItemState[] {
    return this.fileInputList.filter(item => item.showInStaging);
  }

  get activeItems(): DocumentUploadFileItemState[] {
    return this.fileInputList.filter(item => item.isActive);
  }

  get completedItems(): DocumentUploadFileItemState[] {
    return this.fileInputList.filter(item => item.isCompleted);
  }

  get teamId(): Nullable<string> {
    return this.resolvedProps.teamId;
  }

  get teamName(): Nullable<string> {
    if (!this.teamId) return null;
    return this.store.teamManager.strictGetTeam(this.teamId).name;
  }

  isHovered: boolean = false;
  isDragHovered: boolean = false;
  readonly dragItems = observable.array<DataTransferItem>();
  expandedBoxes: string[] = [];

  getIsExpanded(id: string): boolean {
    return this.expandedBoxes.findIndex(el => el === id) > -1;
  }

  expandProgressBox = (id: string) => {
    this.expandedBoxes.push(id);
  }

  collapseProgressBox = (id: string) => {
    remove(this.expandedBoxes, el => el === id);
  }

  toggleProgressBox = (id: string) => {
    if (this.expandedBoxes.findIndex(el => el === id) > -1)
      this.collapseProgressBox(id);
    else
      this.expandProgressBox(id);
  }

  get dragError(): boolean {
    return !!this.dragErrorMessage;
  }

  get dragErrorMessage(): Nullable<string> {
    return documentDragErrorMessage([...this.dragItems]);
  }

  get dragDropMessage(): Nullable<string> {
    const items = [...this.dragItems];
    if (items.length === 0)
      return `Drop files here`;
    return `Drop ${pluralize(items.length, 'file', 'files')} here`;
  }

  readonly pageSessionTasks = observable.array<UploadTask>();

  get teamGroupedTasks(): UploadTaskGroup[] {
    const criteria: GroupCriteria = 'teamId';
    const groups = groupBy(this.pageSessionTasks, criteria);
    return Object.keys(groups).map(key => {
      const value = groups[key];
      const taskGroup = new UploadTaskGroup(this.store, {
        groupBy: criteria,
        groupId: key,
        tasks: value
      });
      return taskGroup;
    });
  }

  get tasksByTeam() {
    return groupBy(this.pageSessionTasks, 'teamId')
  }

  get taskTeamIds() {
    return Object.keys(this.tasksByTeam);
  }

  readonly fileInputList: IObservableArray<DocumentUploadFileItemState> = createArray<DocumentUploadFileItemState>(true) as IObservableArray;

  readonly uploadButton: InputState = input(this, {
    name: 'uploadButton',
    disabled: () => {
      return (
        this.form?.isSubmitting ||
        this.form?.hasTouchedErrorInputs
      );
    }
  });

  get showStagingForm(): boolean {
    return this.stagedItems.length > 0;
  }

  get showProgressBox(): boolean {
    return this.teamGroupedTasks && this.teamGroupedTasks.length > 0;
  }

  get stagingBoxesCount(): number {
    let counter = 0;
    if (this.showStagingForm)
      counter += 1;
    counter += this.teamGroupedTasks.length;
    return counter;
  }

  handlePointerEnter(_evt: PointerEvent) {
    this.isHovered = true;
  }
  handlePointerLeave(_evt: PointerEvent) {
    this.isHovered = false;
  }
  handleFileChange(evt: ChangeEvent<HTMLInputElement>) {
    this.setUploadInput(evt.target.files);
    // @ts-expect-error -- resetting input.value to null clears the file selection; string type doesn't allow null
    evt.target.value = null;
  }
  handleFileClick(_evt: PointerEvent) { /* noop */ }

  handleDragEnter(evt: DragEvent) {
    this.isDragHovered = true;
    const items = evt.dataTransfer?.items;
    if (items && items.length > 0) {
      this.dragItems.replace([...items]);
    }
  }

  handleDragLeave(_evt: DragEvent) {
    this.isDragHovered = false;
  }
  handleDrop(_evt: DragEvent) {
    this.isDragHovered = false;
    this.dragItems.clear();
  }

  setUploadInput(fileList?: FileList | null) {
    if (!this.store) {
      console.warn('Cannot begin upload without a store.');
      return;
    }

    if (!fileList || fileList.length === 0)
      return;

    const inputFiles = [...fileList];

    const nonDocumentFiles = inputFiles.filter(file => !isDocumentFile(file));
    if (nonDocumentFiles.length > 0) {
      this.dispatch(
        'NotificationService',
        'notifyError',
        pluralize(
          nonDocumentFiles.length,
          `file is not a supported document (${SUPPORTED_DOCUMENT_TYPES_LABEL})`,
          `files are not supported documents (${SUPPORTED_DOCUMENT_TYPES_LABEL})`));
      return;
    }

    inputFiles.forEach(file => this.addFileItem(file));
  }

  addFileItem(file: File) {
    const fileInputItem: DocumentUploadFileItemState = new DocumentUploadFileItemState(this.store, {
      teamId: () => this.teamId,
      file
    });
    this.fileInputList.push(fileInputItem);
    this.store.documentUploadGuidedTraining.handleDocumentFileSelected('translation');
  }

  removeFileItem(id: string) {
    const itemIndex = this.fileInputList.findIndex(item => item.id === id);
    if (itemIndex > -1)
      this.fileInputList.splice(itemIndex, 1);
  }

  async submit(): Promise<UploadTask[] | null> {
    if (!this.form)
      return null;

    this.form.handleSubmit();

    if (this.form.error) {
      this.form.handleSubmitReject();
      return null;
    }

    const tasks = this.stagedItems.map(item => item.submit());
    tasks.forEach(task => {
      this.expandProgressBox(task.teamId || 'null');
      this.pageSessionTasks.push(task)
    });

    this.store.documentUploadGuidedTraining.handleWorkflowUploadStarted(
      'translation',
      tasks[0]?.id ?? null,
      this.teamId ?? null,
    );

    this.form.handleSubmitResolve();
    return tasks;
  }

  init() {
    const contextTasks = this.store?.uploadService?.tasks.filter(task => task.isActive);
    this.pageSessionTasks.push(...contextTasks);
    this.taskTeamIds.forEach(id => this.expandProgressBox(id));
  }

  reset() {
    this.pageSessionTasks.clear();
    this.form?.clear();
    this.fileInputList.clear();
  }
}
